import { Alert } from '@/components/ui/alert';
import { metaStateFromAuthorizationStatus } from '@/components/display-rights/display-rights-status';
import { Box, Button, Flex, Skeleton, VStack } from '@chakra-ui/react';
import {
  MentionBoostDestinationType,
  MentionBoostEligibilityStatus,
  MentionBoostPlacementMode,
  MentionBoostSetupAudienceMode,
} from '@fluenceur/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BoostFooter } from './BoostFooter';
import { resolveDeliveryFooterIntent } from './boost-footer-intent';
import { AdAccountField } from './boost-setup/AdAccountField';
import { AdAccountPaymentNotice } from './boost-setup/AdAccountPaymentNotice';
import { AudienceControls } from './boost-setup/AudienceControls';
import { BoostDsaBeneficiaryForm } from './boost-setup/BoostDsaBeneficiaryForm';
import { BoostReadinessNotice } from './boost-setup/BoostReadinessNotice';
import { BudgetControls } from './boost-setup/BudgetControls';
import { DestinationSelector } from './boost-setup/DestinationSelector';
import { PlacementField } from './boost-setup/PlacementField';
import { ProductDestination } from './boost-setup/ProductDestination';
import { useAdAccountPick } from './boost-setup/useAdAccountPick';
import { useMentionBoostPlacement } from './boost-setup/useMentionBoostPlacement';
import { SetupSection } from './boost-setup/SetupSection';
import { UtmControls } from './boost-setup/UtmControls';
import { shortDestinationLabel } from './boost-setup/utils';
import { useMentionBoostLaunch } from './boost-setup/useMentionBoostLaunch';
import {
  useMentionBoostSetupForm,
  budgetPresets,
  durationPresets,
} from './boost-setup/useMentionBoostSetupForm';
import { WebsiteDestination } from './boost-setup/WebsiteDestination';
import { BoostAppliedUtm } from './BoostAppliedUtm';
import { MentionBoostLaunchConfirmDialog } from './MentionBoostLaunchConfirmDialog';
import { MentionBoostLaunchedView } from './MentionBoostLaunchedView';
import { StopBoostConfirmDialog } from './StopBoostConfirmDialog';
import { useBoostStopFlow } from './useBoostStopFlow';

const SETUP_FOOTER_FADE = 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.88))';

interface MentionBoostSetupPanelProps {
  isOpen: boolean;
  mentionId: string;
  // Creator handle, used to derive the default UTM convention.
  creatorUsername?: string;
  onClose: () => void;
}

export function MentionBoostSetupPanel({
  isOpen,
  mentionId,
  creatorUsername,
  onClose,
}: MentionBoostSetupPanelProps) {
  const { t, i18n } = useTranslation();
  // The pick sits above the setup query: the readiness that query returns is
  // resolved against one account, so the choice has to reach the server for its
  // blockers to describe the account being billed.
  const { pickedAdAccountId, pickAdAccount } = useAdAccountPick(mentionId);
  const form = useMentionBoostSetupForm({
    isOpen,
    mentionId,
    preferredAdAccountId: pickedAdAccountId,
    creatorUsername,
    onClose,
  });
  const stopFlow = useBoostStopFlow(mentionId);
  const isEligible =
    form.eligibility?.status === MentionBoostEligibilityStatus.READY &&
    !!form.eligibility?.meta.canManageBoost;
  const authorizationStatus = form.eligibility?.authorization.current?.status ?? null;
  const displayRights = form.eligibility?.authorization.displayRights ?? null;
  const launch = useMentionBoostLaunch({
    mentionId,
    pickedAdAccountId,
    hasSetup: !!form.setup,
    isEligible,
    isSetupValid: form.canSave && !form.isLoading,
    launchedBoost: form.launchedBoost,
    readiness: form.readiness,
    displayRights,
    authorizationStatus,
  });
  const { isDirty, saveDraft, placementMode, setPlacement } = form;
  const { openConfirm } = launch;

  // Placement lives outside the form: the expand state, the campaign search and
  // the two read queries are UI concerns, while the form stays the single owner
  // of the persisted payload.
  const placement = useMentionBoostPlacement({
    mentionId,
    isOpen,
    adAccountId: launch.selectedAdAccountId,
    placementMode: form.placementMode,
    placementCampaignId: form.placementCampaignId,
    onPlacementChange: form.setPlacement,
  });

  const estimatedEndDate = useMemo(() => {
    const estimatedEnd = new Date();
    estimatedEnd.setDate(estimatedEnd.getDate() + form.durationDays);
    return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(estimatedEnd);
  }, [form.durationDays, i18n.language]);

  // Campaigns belong to ONE ad account. Changing the billing account therefore
  // invalidates any campaign already chosen: reset the placement rather than
  // keep sending a campaign id the new account does not own (the launch would
  // refuse it, but the brand would only find out at launch time).
  const handleAdAccountChange = useCallback(
    (adAccountId: string) => {
      pickAdAccount(adAccountId);
      if (placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN) {
        setPlacement(MentionBoostPlacementMode.NEW_CAMPAIGN, null);
      }
    },
    [pickAdAccount, placementMode, setPlacement]
  );

  // The nudge to go connect an account belongs to the case where there is none
  // to select at all. Everything downstream of that (nothing chosen yet, no
  // payment method, no DSA declaration) has its own inline surface, so the
  // notice would only repeat them. Readiness is computed once a setup exists;
  // until then we neither nudge nor offer launch.
  const showReadinessNotice =
    !!form.readiness && (!launch.hasSelectableAdAccount || launch.needsCurrencyCompatibleAdAccount);

  // The launch reads the server-side setup, so the latest edits must be persisted
  // before the confirm opens. If the draft is dirty we await the save; on save
  // failure (already toasted) we do not open the confirm.
  const handleLaunch = useCallback(async () => {
    if (placement.isSelectionUnresolved || placement.isResolvingSelection) {
      return;
    }
    try {
      if (isDirty) {
        await saveDraft();
      }
      openConfirm();
    } catch {
      // Save failed and surfaced its own error toast; stay on the form.
    }
  }, [
    isDirty,
    openConfirm,
    placement.isResolvingSelection,
    placement.isSelectionUnresolved,
    saveDraft,
  ]);

  // A launched boost that is not relaunchable is read-only: show the honest
  // delivery state, never the editable form.
  if (launch.hasActiveLaunchedBoost && form.launchedBoost) {
    return (
      <Flex flex="1" minH={0} direction="column" position="relative">
        <Box flex="1" overflowY="auto" pb={{ base: 24, md: 28 }}>
          <MentionBoostLaunchedView boost={form.launchedBoost} />
        </Box>
        <BoostFooter
          intent={resolveDeliveryFooterIntent(
            form.launchedBoost.deliveryState,
            stopFlow.openConfirm
          )}
        />
        <StopBoostConfirmDialog
          isOpen={stopFlow.isConfirmOpen}
          isStopping={stopFlow.isStopping}
          onConfirm={stopFlow.confirmStop}
          onCancel={stopFlow.cancelConfirm}
          parentCampaignName={form.launchedBoost.config.parentCampaignName ?? null}
        />
      </Flex>
    );
  }

  return (
    <Flex flex="1" minH={0} direction="column" position="relative">
      <Box flex="1" overflowY="auto" px={{ base: 4, md: 6 }} pt={5} pb={{ base: 28, md: 32 }}>
        {form.isLoading ? (
          <VStack align="stretch" gap={4}>
            <Skeleton h="44px" rounded="md" />
            <Skeleton h="96px" rounded="md" />
            <Skeleton h="136px" rounded="md" />
          </VStack>
        ) : form.isError ? (
          <Alert status="warning" title={t('mentions.boost.setup.unavailableTitle')}>
            {t('mentions.boost.setup.unavailableDescription')}
          </Alert>
        ) : (
          <VStack align="stretch" gap={0}>
            {showReadinessNotice && (
              <Box mb={4}>
                <BoostReadinessNotice readiness={form.readiness} />
              </Box>
            )}

            {launch.showAdAccountSelect && (
              <SetupSection
                title={t('mentions.boost.setup.adAccount.title')}
                description={
                  launch.selectedAdAccountId
                    ? undefined
                    : t('mentions.boost.setup.adAccount.selectionRequired')
                }
                isFirst
              >
                <AdAccountField
                  adAccounts={launch.adAccounts}
                  selectedId={launch.selectedAdAccountId}
                  onChange={handleAdAccountChange}
                />
              </SetupSection>
            )}

            {/* Outside the select branch: a brand owning a single account never
                sees the picker, and its missing payment method still has to be
                said. */}
            {launch.needsPaymentMethod && launch.selectedAdAccount && (
              <Box mb={4}>
                <AdAccountPaymentNotice account={launch.selectedAdAccount} />
              </Box>
            )}

            {launch.needsDsaBeneficiary && form.readiness && (
              <SetupSection title={t('mentions.boost.setup.dsa.title')} isFirst>
                <BoostDsaBeneficiaryForm
                  mentionId={mentionId}
                  beneficiary={form.readiness.dsaBeneficiary}
                  payor={form.readiness.dsaPayor}
                />
              </SetupSection>
            )}

            {/* Placement sits with the account it bills against, before the
                budget it can hand over to a campaign. */}
            <Box pb={5}>
              <PlacementField
                placement={placement}
                placementCampaignName={form.placementCampaignName}
                productHandle={form.selectedProduct?.handle ?? null}
                productTitle={form.selectedProduct?.title ?? null}
                creatorUsername={creatorUsername ?? ''}
              />
            </Box>

            <SetupSection title={t('mentions.boost.setup.budgetAndDuration')} isFirst>
              <BudgetControls
                budgetAmountInCents={form.budgetAmountInCents}
                budgetCurrencyCode={form.budgetCurrencyCode}
                budgetCurrencyDisplay={form.budgetCurrencyDisplay}
                budgetWholeUnitInvalid={form.budgetWholeUnitInvalid}
                durationCollection={form.durationCollection}
                durationDays={form.durationDays}
                onBudgetChange={form.handleBudgetChange}
                onDurationChange={form.handleDurationChange}
                budgetPresets={budgetPresets}
                durationPresets={durationPresets}
                // `campaignBudgetManaged` is only ever true once the campaign
                // is resolved, so its name is always available here.
                campaignBudgetName={
                  placement.campaignBudgetManaged
                    ? (placement.selectedCampaign?.name ?? null)
                    : null
                }
                // While the stored campaign is being resolved, showing the
                // manual amount would flash a control that may not apply.
                isResolvingCampaignBudget={placement.isResolvingSelection}
              />
            </SetupSection>

            <SetupSection title={t('mentions.boost.setup.audienceLabel')}>
              <AudienceControls
                advantagePlusEnabled={form.advantagePlusEnabled}
                advantagePlusAgeCollection={form.advantagePlusAgeCollection}
                advantagePlusMinimumAgeInvalid={form.advantagePlusMinimumAgeInvalid}
                ageCollection={form.ageCollection}
                advantagePlusHelperText={t('mentions.boost.setup.advantagePlusHelper')}
                advantagePlusMinimumAgeError={t(
                  'mentions.boost.setup.advantagePlusMinimumAgeError'
                )}
                advantagePlusLabel={t('mentions.boost.setup.advantagePlus')}
                ageRangeLabel={t('mentions.boost.setup.ageRangeLabel')}
                locationsLabel={t('mentions.boost.setup.locationsLabel')}
                minimumAgeLabel={t('mentions.boost.setup.minimumAgeLabel')}
                onAdvantagePlusChange={form.handleAdvantagePlusChange}
                onMaxAgeChange={form.handleTargetAgeMaxChange}
                onMinAgeChange={form.handleTargetAgeMinChange}
                onTargetLocationsChange={form.setTargetLocations}
                targetAgeRange={form.targetAgeRange}
                targetLocations={form.targetLocations}
              />
            </SetupSection>

            <SetupSection title={t('mentions.boost.setup.destination.sectionLabel')}>
              <DestinationSelector
                isShopifyConnected={form.isShopifyConnected}
                onChange={form.setDestinationType}
                value={form.destinationType}
              />

              {form.destinationType === MentionBoostDestinationType.PRODUCT_PAGE && (
                <ProductDestination
                  customTargetUrlEnabled={form.customTargetUrlEnabled}
                  onCustomTargetUrlChange={form.handleCustomTargetUrlChange}
                  onProductToggle={form.handleProductToggle}
                  onTargetUrlChange={form.setTargetUrl}
                  selectedProduct={form.selectedProduct}
                  selectedProductIds={form.selectedProductIds}
                  targetUrl={form.targetUrl}
                  targetUrlIsInvalid={form.targetUrlIsInvalid}
                  targetUrlPreview={form.targetUrlPreview}
                />
              )}

              {form.destinationType === MentionBoostDestinationType.WEBSITE && (
                <WebsiteDestination
                  onUrlChange={form.setWebsiteUrl}
                  url={form.websiteUrl}
                  urlIsInvalid={form.websiteUrlIsInvalid}
                />
              )}
            </SetupSection>

            {form.showUtmSection && form.utmDestinationUrl && (
              <UtmControls
                pristine={form.isUtmConventionPristine && form.utmLockedKeys.length === 0}
                parameters={form.utmDisplayParameters}
                lockedKeys={form.utmLockedKeys}
                previewUrl={form.utmPreviewUrl}
                urlTags={form.utmUrlTags}
                onValueChange={form.onUtmValueChange}
                onKeyChange={form.onUtmKeyChange}
                onAddParameter={form.onUtmAddParameter}
                onRemoveParameter={form.onUtmRemoveParameter}
                onDisableTracking={form.onUtmDisableTracking}
                onEnableTracking={form.onUtmEnableTracking}
              />
            )}

            {/* A stopped or failed boost returns to the editable form, but what
                it actually applied stays consultable: that is the after-the-fact
                reconciliation the brand does against its analytics. */}
            {form.launchedBoost?.launchedAt && (
              <BoostAppliedUtm
                title={t('mentions.boost.detail.utmPreviousTitle')}
                parameters={form.launchedBoost.config.utmParameters ?? []}
                targetUrl={form.launchedBoost.config.targetUrl}
              />
            )}
          </VStack>
        )}
      </Box>

      <Box
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        zIndex={2}
        px={{ base: 4, md: 6 }}
        pt={4}
        pb={4}
        bg="rgba(255,255,255,0.88)"
        backdropFilter="blur(8px)"
        _before={{
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          top: '-44px',
          h: '44px',
          bg: SETUP_FOOTER_FADE,
          backdropFilter: 'blur(3px)',
          pointerEvents: 'none',
        }}
      >
        <Button
          position="relative"
          zIndex={1}
          width="full"
          colorPalette="blue"
          data-testid="mention-boost-launch-cta"
          loading={launch.isLaunching || form.isSaving}
          disabled={
            !launch.canLaunch || placement.isSelectionUnresolved || placement.isResolvingSelection
          }
          onClick={handleLaunch}
        >
          {t('mentions.boost.launch.cta')}
        </Button>
      </Box>

      <MentionBoostLaunchConfirmDialog
        isOpen={launch.isConfirmOpen}
        submitting={launch.isLaunching}
        canConfirm={
          launch.canConfirmLaunch &&
          !placement.isSelectionUnresolved &&
          !placement.isResolvingSelection
        }
        destination={
          form.utmDestinationUrl
            ? {
                label: shortDestinationLabel(form.utmDestinationUrl),
                utmAppliedCount: form.utmAppliedCount,
                utmIsConvention: form.isUtmConventionPristine,
              }
            : null
        }
        configuration={{
          billedAccount: launch.selectedAdAccount,
          budgetAmountInCents: form.budgetAmountInCents,
          budgetCurrencyCode: form.budgetCurrencyCode,
          durationDays: form.durationDays,
          estimatedEndDate,
          placementMode: form.placementMode,
          placementCampaignName: form.placementCampaignName,
          campaignBudgetManaged: placement.campaignBudgetManaged,
          audienceMode: form.audienceMode,
          targetLocations: form.targetLocations,
          targetAgeRange:
            form.audienceMode === MentionBoostSetupAudienceMode.ADVANTAGE_PLUS
              ? { min: form.targetAgeRange.min, max: 65 }
              : form.targetAgeRange,
        }}
        gate={launch.displayRightsGate}
        displayRights={displayRights}
        metaState={
          authorizationStatus ? metaStateFromAuthorizationStatus(authorizationStatus) : undefined
        }
        offPlatformAttestation={launch.offPlatformAttestation}
        onOffPlatformAttestationChange={launch.setOffPlatformAttestation}
        onClose={launch.closeConfirm}
        onConfirm={launch.confirmLaunch}
      />
    </Flex>
  );
}
